/**
 * The offline database. Deliberately dependency-free: this module is bundled
 * into both the app and the service worker, so it must never touch `window`,
 * `document` or `localStorage`.
 */

export const DB_NAME = 'staketory-offline';
export const DB_VERSION = 1;

export const STORE_OUTBOX = 'outbox';
export const STORE_BLOBS = 'blobs';
export const STORE_RECORDS = 'records';
export const STORE_LISTS = 'lists';
export const STORE_META = 'meta';

export type StoreName =
  | typeof STORE_OUTBOX
  | typeof STORE_BLOBS
  | typeof STORE_RECORDS
  | typeof STORE_LISTS
  | typeof STORE_META;

export const hasIndexedDB = (): boolean =>
  typeof indexedDB !== 'undefined' && indexedDB !== null;

let dbPromise: Promise<IDBDatabase> | null = null;

/** Open (and if needed create) the database. One connection per context. */
export function openDb(): Promise<IDBDatabase> {
  if (!hasIndexedDB()) {
    return Promise.reject(new Error('IndexedDB is not available'));
  }
  if (!dbPromise) {
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
          const outbox = db.createObjectStore(STORE_OUTBOX, { keyPath: 'id' });
          outbox.createIndex('createdAt', 'createdAt');
        }
        if (!db.objectStoreNames.contains(STORE_BLOBS)) {
          db.createObjectStore(STORE_BLOBS, { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains(STORE_RECORDS)) {
          const records = db.createObjectStore(STORE_RECORDS, { keyPath: 'key' });
          records.createIndex('lastAccessAt', 'lastAccessAt');
        }
        if (!db.objectStoreNames.contains(STORE_LISTS)) {
          const lists = db.createObjectStore(STORE_LISTS, { keyPath: 'key' });
          lists.createIndex('lastAccessAt', 'lastAccessAt');
        }
        if (!db.objectStoreNames.contains(STORE_META)) {
          db.createObjectStore(STORE_META, { keyPath: 'key' });
        }
      };
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => {
          db.close();
          dbPromise = null;
        };
        resolve(db);
      };
      request.onerror = () => reject(request.error ?? new Error('Could not open the offline database'));
      request.onblocked = () => reject(new Error('The offline database is blocked by another tab'));
    }).catch((error) => {
      dbPromise = null;
      throw error;
    });
  }
  return dbPromise;
}

/** Forget the cached connection (tests, and after a wipe). */
export function resetDb(): void {
  dbPromise = null;
}

function run<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

async function withStore<T>(
  store: StoreName,
  mode: IDBTransactionMode,
  work: (objectStore: IDBObjectStore) => Promise<T> | T,
): Promise<T> {
  const db = await openDb();
  const tx = db.transaction(store, mode);
  const result = await work(tx.objectStore(store));
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
  });
  return result;
}

export const idbGet = <T>(store: StoreName, key: IDBValidKey): Promise<T | undefined> =>
  withStore(store, 'readonly', (objectStore) => run<T | undefined>(objectStore.get(key) as IDBRequest<T | undefined>));

export const idbGetAll = <T>(store: StoreName): Promise<T[]> =>
  withStore(store, 'readonly', (objectStore) => run<T[]>(objectStore.getAll() as IDBRequest<T[]>));

export const idbPut = <T>(store: StoreName, value: T): Promise<void> =>
  withStore(store, 'readwrite', async (objectStore) => {
    await run(objectStore.put(value as any));
  });

export const idbDelete = (store: StoreName, key: IDBValidKey): Promise<void> =>
  withStore(store, 'readwrite', async (objectStore) => {
    await run(objectStore.delete(key));
  });

export const idbClear = (store: StoreName): Promise<void> =>
  withStore(store, 'readwrite', async (objectStore) => {
    await run(objectStore.clear());
  });

export const idbCount = (store: StoreName): Promise<number> =>
  withStore(store, 'readonly', (objectStore) => run<number>(objectStore.count()));

/** Small key/value corner: auth token mirror, sync lock, last sync time. */
export async function metaGet<T>(key: string): Promise<T | undefined> {
  const row = await idbGet<{ key: string; value: T }>(STORE_META, key);
  return row?.value;
}

export const metaSet = <T>(key: string, value: T): Promise<void> =>
  idbPut(STORE_META, { key, value });

export const metaDelete = (key: string): Promise<void> => idbDelete(STORE_META, key);
